import React, {useCallback, useEffect, useMemo, useState} from 'react';
import history from '../../history';
import {useDispatch, useSelector} from 'react-redux';
import {ReduxState} from '../../store/types';
import {OptionsTemplateInfo, UAInfo, UpdateOptionsTemplateRequest} from '../../store/types/Devices';
import Loader from '../../components/Loader/Loader';
import {actions} from '../../store';
import {Routes} from '../../routes/routes';
import usePageTitle from '../../hooks/usePageTitle';
import PermissionPlaceholder from "../../components/PermissionProvider/PermissionPlaceholder";
import {Permission, PermissionType} from "../../store/types/Permission";
import FormActions from '../../components/Forms/FormActions';
import {usePermissions, useRawPermissions} from '../../hooks/usePermissions';
import {useTranslation} from 'react-i18next';
import AlertDialog from '../../components/AlertDialog/AlertDialog';
import {DialogButton} from '../../components/AlertDialog/DialogContainer';
import PreventLeavingPage from '../../components/AlertDialog/PreventLeavingPage';
import {DeviceDetailsProps, useStyles} from './DeviceDetails.utils';
import NotFound from '../NotFound/NotFound';
import PermissionProvider from '../../components/PermissionProvider/PermissionProvider';
import {getFreeLines} from "./FreeLines";
import {showToast} from "../../utils/showToast";
import {generateTabs} from '../../utils/generateTabs';
import DeviceDetailsTab from './DeviceDetailsTab';
import DetailsWrapper from '../../components/DetailsWraper/DetailsWrapper';
import DeviceTitle from '../../components/Devices/DeviceTitle';
import PortsConfigList from '../../components/Devices/PortsConfigList';
import DeviceProfileDetailsTab from '../DeviceProfiles/DeviceProfileDetailsTab';
import {IndividualType} from '../../store/types/CustomerInfo';
import {Formik} from 'formik';
import {
    DeviceProfileDetailsTabFormType,
    deviceProfileValidationSchema,
    useDeviceProfileDetailsInitialDate
} from '../DeviceProfiles/DeviceProfileDetailsTab.utils';
import {YesNo} from '../../store/types/CallScreening';

const DeviceDetails: React.FC<DeviceDetailsProps> = ({id}) => {
    const {t} = useTranslation();
    usePageTitle();
    const dispatch = useDispatch();
    const classes = useStyles();
    const editPagePermission = usePermissions(...Permission.Inventory.Devices.DeviceDetails.value);
    const permissions = useRawPermissions();

    const {items, isLoading, isReleasingDevice, isEditing} = useSelector(
        (state: ReduxState) => state.devices,
    );

    const [showLoader, setShowLoader] = useState(false)
    const [device, setDevice] = useState<UAInfo | undefined>();
    const [form, setForm] = useState<UAInfo | undefined>();
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [validate, setValidate] = useState<boolean>(false);
    const [isSetDeviceComplete, setIsSetDeviceComplete] = useState<boolean>(false);
    const [invalidDeviceId, setInvalidDeviceId] = useState<boolean>(false);
    const [deleteDialog, setDeleteDialog] = useState<{
        isOpen: boolean;
        i_ua?: number;
        name?: string;
    }>({
        isOpen: false
    });
    const i_customer: number = useSelector((state: ReduxState) => state.generic?.sessionData?.i_customer ?? 0);
    const [templateInfo, setTemplateInfo] = useState<OptionsTemplateInfo | undefined>();
    const [isDirtyTemplateInfo, setIsDirtyTemplateInfo] = useState<boolean>(false);
    const userType = useSelector((state: ReduxState) => state.generic.sessionData?.individual_type || IndividualType.Manager);
    const isSupervisor = userType == IndividualType.Supervisor;
    
    const {
        profileDetails,
        uaProfileMetaInfo,
        templates,
        isLoadingTemplates,
        isLoadingMetadata,
        isTypeListLoading,
        isUpdatingOptionsTemplate,
        isLoadingProfileDetails,
        phoneDirectories,
        ua_type_list
    } = useSelector((state: ReduxState) => state.devices);

    useEffect(() => {
        if(profileDetails) {
            setTemplateInfo(profileDetails);
        }
    }, [profileDetails, setTemplateInfo]);

    const isLoadingDeviceProfileTabData = useMemo(() => {
        return isLoadingMetadata 
            || isTypeListLoading 
            || isLoadingTemplates
            || !i_customer 
            || isUpdatingOptionsTemplate
            || isLoadingProfileDetails;
    }, [isLoadingMetadata, isTypeListLoading, isLoadingTemplates, i_customer, 
            isUpdatingOptionsTemplate, isLoadingProfileDetails]);

    useEffect(() => {
        if (id && items == undefined) {
            dispatch(
                actions.devicesList.request({
                    i_ua: id,
                    with_available_ports: 1,
                    with_busy_ports: 1,
                })
            );
        }
    }, [id, items]);
    
    useEffect(() => {
        if (items) {
            const selected = items?.find((o) => o.i_ua == id);
            selected && setDevice(selected);

            if (selected == undefined) {
                setInvalidDeviceId(true);
            }
            setIsSetDeviceComplete(true);
        }
    }, [items]);

    const handleDeleteAction = () => {
        dispatch(actions.deleteUA.request({
                i_ua: deleteDialog.i_ua || 0,
                callback: () => {
                    closeDeleteDialog();
                    showToast(t<string>('screens:devices.deviceDeleted'));
                    //@ts-ignore
                    window.history.replaceState(null, null, '/');
                    history.push(Routes.Devices);
                }
            }
        ));
        setIsDirty(false);
        setIsDirtyTemplateInfo(false);
    };

    const closeDeleteDialog = () => {
        setDeleteDialog({
            isOpen: false,
            i_ua: undefined,
            name: undefined
        });
    };

    const onChevronClick = () => {
        history.push(Routes.Devices);
    };

    const handleUpdateAction = useCallback(() => {
        setShowLoader(true);
        
        //step 1: update device template (if needed). If updated - fetch templates list asynhroniously
        //step 2 - when first done: update device details
        //refresh data and navigate to list / ingnoring pending IsDirty/isDirtyTemplateInfo flags

        const showToastAndNavigate = () => {
            showToast(t<string>('screens:devices.deviceUpdated'));
            setShowLoader(false);

            setIsDirty(false);
            setIsDirtyTemplateInfo(false);
            dispatch(
                actions.devicesList.request({
                    i_ua: id,
                    with_available_ports: 1,
                    with_busy_ports: 1,
                }),
            );
        };

        const updateDeviceStep2 = () => {
            if(isDirty && form) {
                dispatch(actions.updateUa.request({
                    object: form,
                    callback: showToastAndNavigate,
                    callbackOnError: () => {
                        setShowLoader(false);
                    }
                }));
            }
            else
            {
                showToastAndNavigate();
            }
        };

        const fetchProfileList = () => {
            dispatch(
                actions.getOptionsTemplateList.request({
                    get_total: 1,
                    i_customer: i_customer,
                    limit: 1000,
                    offset: 0
                })
            );
            updateDeviceStep2();
        };

        if(isDirtyTemplateInfo && templateInfo)
        {
            const i_ua = form?.i_ua ?? +id;
            const initialDeviceList = templateInfo?.i_ua_list ?? [];
            const addedDevices = [i_ua];

            const updTemplates: UpdateOptionsTemplateRequest[] = [];
            const removeFromTemplate = templates?.find(e => e.i_ua_list?.find(c => c === i_ua));
            if(removeFromTemplate) {
                updTemplates.push({
                    template_info: {
                        i_ua_list: removeFromTemplate?.i_ua_list?.filter(e => e !== i_ua) ?? [],
                        i_ua_type: removeFromTemplate.i_ua_type,
                        name: removeFromTemplate.name,
                        i_ua_config_template: removeFromTemplate.i_ua_config_template
                    },
                    callback: fetchProfileList
                });
            }

            const payload: UpdateOptionsTemplateRequest = {
                template_info: {
                    i_ua_list: [...initialDeviceList, ...addedDevices],
                    i_ua_type: templateInfo.i_ua_type,
                    name: templateInfo.name,
                    i_ua_config_template: templateInfo.i_ua_config_template
                },
                callback: fetchProfileList
            };
            updTemplates.push(payload);

            dispatch(actions.updateOptionsTemplate.request(updTemplates));
        }
        else if (!form) {
            setShowLoader(false);
            showToastAndNavigate();
            return;
        }
        else
        {
            updateDeviceStep2();
        }
    }, [form, isDirtyTemplateInfo, templateInfo, templates, i_customer]);

    const portedDeviceOnSave = useCallback((isValid: boolean) => {
        setValidate(false);
        if (isValid) handleUpdateAction();
    }, [setValidate, handleUpdateAction]);

    let allLinesFree = false;
    if (device) {
        const freeLines = getFreeLines(device);
        allLinesFree = freeLines.left == freeLines.right;
    }
    
    useEffect(() => {
        if(device?.i_ua && templates === undefined && !isLoadingTemplates && i_customer) {
            dispatch(
                actions.getOptionsTemplateList.request({
                    i_customer: i_customer,
                    get_total: 1,
                    limit: 1000,
                    offset: 0
                })
            );
        }
    }, [templates, device, isLoadingTemplates, i_customer]);

    useEffect(() => {
        if (device?.i_ua && device?.i_ua_type && profileDetails === undefined && i_customer
            && ua_type_list && ua_type_list.find(e => e.i_ua_type === device?.i_ua_type)
        ) {
            dispatch(actions.getProfileTemplate.request({
                i_customer: i_customer,
                i_ua: device?.i_ua
            }));
        }
    }, [device, profileDetails, i_customer, ua_type_list]);

    useEffect(() => {
        if(profileDetails?.i_ua_type && !isSupervisor)
        {
            dispatch(
                actions.getUaProfileMetaInfo.request({
                    i_ua_type: profileDetails.i_ua_type,
                })
            );
            dispatch(
                actions.getDevicePhoneBookList.request({
                    get_total: 1,
                    get_total_records: YesNo.Yes,
                    i_customer: i_customer,
                    limit: 1000,
                    offset: 0,
                })
            );
        }
    }, [profileDetails, isSupervisor]);

    const isShowProfileTab = useMemo(() => {
        return ua_type_list && device && !isSupervisor &&
            ua_type_list.find(e => e.i_ua_type == device.i_ua_type);
    }, [ua_type_list, device, isSupervisor])

    const initFormData = useMemo(() => {
        return useDeviceProfileDetailsInitialDate(false, templateInfo ?? null, t, uaProfileMetaInfo, phoneDirectories, ua_type_list);
    }, [profileDetails, t, uaProfileMetaInfo, ua_type_list]);
    
    const tabsArray = useMemo(() => {
        const array = [
            {
                title: t('common:general'),
                permission: Permission.Inventory.Devices.DeviceDetails.value,
                tab: (
                    <DeviceDetailsTab
                        device={device}
                        setForm={setForm}
                        validate={validate}
                        setIsDirty={setIsDirty}
                        portedDeviceOnSave={(isValid) => {
                            portedDeviceOnSave(isValid);
                        }}    
                    />
                ),
            },
            {
                title: t('screens:devices.lines'),
                permission: Permission.Inventory.Devices.DeviceDetails.value,
                tab: (
                    <PortsConfigList
                        device={device}
                        className={classes.devicePorts}
                        isLoading={isLoading}
                    />
                ),
            }
        ];
        if(isShowProfileTab) {
            array.push(
                {
                    title: t('screens:devices.profile'),
                    permission: Permission.Inventory.Devices.DeviceDetails.value,
                    tab: (
                        <Formik<DeviceProfileDetailsTabFormType>
                            initialValues={initFormData}
                            validationSchema={deviceProfileValidationSchema}
                            onSubmit={()=>{;}}
                            enableReinitialize
                            validateOnChange={false}
                            validateOnBlur
                        >
                            <DeviceProfileDetailsTab
                                uaProfileMetaInfo={uaProfileMetaInfo} 
                                readonly
                                onChangeTemplate={(t: OptionsTemplateInfo) => {
                                    setIsDirtyTemplateInfo(true);
                                    dispatch(
                                        actions.getProfileTemplate.request({
                                            i_customer: i_customer,
                                            i_ua_config_template: t.i_ua_config_template
                                        })
                                    );
                                }}
                                iUaType={device?.i_ua_type}
                            />
                        </Formik>
                    ),
                }
            );
        }
        return array;
    }, [device, setForm, validate, setIsDirty, portedDeviceOnSave, isLoading, templateInfo, 
        uaProfileMetaInfo, setIsDirtyTemplateInfo, isShowProfileTab, ua_type_list, phoneDirectories]);

    const isSaveActive = useMemo(() => {
        return (isDirty || isDirtyTemplateInfo) && !(isShowProfileTab && isLoadingDeviceProfileTabData);
    }, [isDirty, isDirtyTemplateInfo, isShowProfileTab, isLoadingDeviceProfileTabData]);

    const {tabNames, tabs} = useMemo(() => generateTabs(
        tabsArray,
        permissions
    ), [tabsArray, permissions]);

    if ((isLoading && !isReleasingDevice && !isEditing && items == undefined) && !isSetDeviceComplete) {
        return (
            <PermissionPlaceholder permission={Permission.Inventory.Devices.DeviceDetails.value}>
                <div className={classes.loader}>
                    <Loader dataQa={'device-details-loader'}/>
                </div>
            </PermissionPlaceholder>)
    }

    return !isLoading && invalidDeviceId ? (
        <NotFound/>
    ) : (
        <PermissionProvider permission={Permission.Inventory.Devices.DeviceDetails.value}>
            <div className={classes.root}>
                {(showLoader) && (
                    <Loader
                        dataQa="edit-ring-device-loader"
                        absolutePosition
                    />
                )}
                <div className={classes.scrollableDeviceDetails}>
                    <DeviceTitle
                        device={device}
                        className={classes.deviceHeader}
                        onChevronClick={onChevronClick}
                    />
                    <DetailsWrapper
                        showActionButtons={false}
                        tabs={tabs}
                        tabsNames={tabNames}
                        headerCustomClass={classes.tabsHeader}
                        doNotControlForms
                        customRootClass={classes.rootDeviceDetailsPageTabRoot}
                    >
                    </DetailsWrapper>
                </div>
                {(device?.ported === 'Y' || isShowProfileTab) && editPagePermission !== PermissionType.Hidden && !isLoading && (
                    <FormActions
                        isSaveActive={isSaveActive}
                        onSave={() => {
                            if(!validate) {
                                setValidate(true);
                            }
                            if(!(device?.ported === 'Y') &&
                                isShowProfileTab && isDirtyTemplateInfo
                            ) {
                                portedDeviceOnSave(true);
                            }
                        }}
                        hideCancel={false}
                        hideDelete={!allLinesFree}
                        editPermission={Permission.Inventory.Devices.DeviceDetails.value}
                        deleteTitle={t('screens:devices.deleteDevice')}
                        onDelete={() => {
                            if (!form) {
                                return;
                            }
                            setDeleteDialog({isOpen: true, i_ua: form.i_ua, name: form.name});
                        }}
                        deleteButtonDisabled={!allLinesFree}
                        deletePermission={Permission.Inventory.Devices.DeviceDetails.DeleteDevice.value}
                        onCancel={onChevronClick}
                    />)}
                <AlertDialog
                    className={classes.deleteDialog}
                    contentClass={classes.contentDialog}
                    isOpen={deleteDialog.isOpen}
                    dataQa={'delete-rule-dialog'}
                    hideHeader
                    description={t('screens:devices.deleteRule', {
                        name: deleteDialog.name || '',
                    })}
                    dialogActionsButtons={[
                        <DialogButton
                            key="cancel"
                            label={t('common:cancel')}
                            onClick={closeDeleteDialog}
                        />,
                        <DialogButton
                            key="delete"
                            label={t('common:delete')}
                            onClick={handleDeleteAction}
                        />,
                    ]}
                />
                <PreventLeavingPage
                    isOpen={isDirty || isDirtyTemplateInfo}
                    onSave={() => {
                        if(!validate) {
                            portedDeviceOnSave(true);
                        }
                    }}
                />
            </div>
        </PermissionProvider>
    );
};

export default DeviceDetails;
